/**
 * The lower bound on an Exchange or Withdraw amount, and the two ways it is
 * allowed to be cleared.
 *
 * The threshold is stored in USDT (`min_amount_exchange`,
 * `min_amount_withdraw`) and shown in whatever the client is selling, so it
 * has to be divided by a price and cut to the token's precision. Both
 * screens used to cut it DOWNWARD — the hint through a truncating formatter,
 * the Exchange prefill through `toFixed`. The number on screen was then
 * worth a hair less than the threshold, the form refused it, and the server
 * would have refused it too: a client was told to enter 0,40492325 AMZN and
 * the button stayed grey on exactly that. Rounding up is the whole fix;
 * the comparison was already ">=".
 *
 * The second rule is new. A client holding less than the minimum could never
 * get rid of it: too little to trade, no way to withdraw. When the whole
 * available balance is under the threshold, emptying it is allowed.
 */

/** Integer scale for comparing token amounts; 8 digits, as the forms use. */
const scaleFor = (decimals: number): number =>
  Math.pow(10, Math.min(Math.max(decimals, 0), 8));

/** value, rounded UP to `decimals` places — never below what it started as. */
export const ceilToDecimals = (value: number, decimals: number): number => {
  const scale = scaleFor(decimals);
  return Math.ceil(value * scale - 1e-9) / scale;
};

/**
 * The USDT threshold expressed in the token being spent, rounded up to that
 * token's precision so the figure shown is one the form and the server both
 * accept.
 *
 * `priceUsd` is 1 when no conversion is needed — a USD-pegged token, or a
 * threshold already denominated in the token's own units. Null, or anything
 * that is not a usable price, means the token cannot be priced and the
 * callers fail open: a rate outage must not stop trading. Passing 1 for
 * "unknown" would quietly demand 100 AMZN of a client who owes 100 USDT
 * worth of them.
 */
export const minimumInToken = (
  minUsdt: number | null,
  priceUsd: number | null,
  decimals: number,
): number | null => {
  if (minUsdt == null) { return null; }
  if (priceUsd == null || !Number.isFinite(priceUsd) || priceUsd <= 0) { return null; }
  return ceilToDecimals(minUsdt / priceUsd, decimals);
};

export interface MinimumCheck {
  /** What the client entered, in the token being sold or withdrawn. */
  amount: number;
  /** The threshold in that same token, from `minimumInToken`. */
  minInToken: number | null;
  /** Amount − locked on the balance being spent. */
  availableBalance: number;
  /**
   * The largest amount this form can accept: the available balance on
   * Exchange, where the fee comes out of the amount, and the balance less
   * the fee on Withdraw, where it is charged on top.
   */
  maxSpendable: number;
  /** The token's precision, so the comparison is not decided by float noise. */
  decimals: number;
}

/**
 * True when the amount may be submitted as far as the minimum is concerned.
 * Says nothing about the balance or the fee — the callers check those.
 */
export const clearsMinimum = ({
  amount, minInToken, availableBalance, maxSpendable, decimals,
}: MinimumCheck): boolean => {
  if (minInToken == null) { return true; }
  const scale = scaleFor(decimals);
  const units = (n: number) => Math.round(n * scale);

  if (units(amount) >= units(minInToken)) { return true; }

  // Dust: everything the client holds is under the threshold, and they are
  // emptying it. Anything less than the whole balance is still refused —
  // splitting dust in half only makes smaller dust.
  //
  // maxSpendable is rounded DOWN: with a fee charged on top it is a balance
  // divided by 1 + rate, and rounding that up would name a figure whose fee
  // overdraws the account. The form would then refuse the only amount this
  // rule allows.
  const maxUnits = Math.floor(maxSpendable * scale + 1e-9);
  return units(availableBalance) < units(minInToken)
    && maxUnits > 0
    && units(amount) >= maxUnits;
};

/**
 * The threshold as a hint, in the token's own precision with trailing zeros
 * dropped: "50", "0,40492325", "1,005". The same number the check uses, so
 * the screen never asks for a figure it will then refuse.
 */
export const formatMinimumHint = (minInToken: number, decimals: number): string => {
  const places = Math.min(Math.max(decimals, 0), 8);
  const fixed = minInToken.toFixed(places);
  const [int, frac = ''] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const trimmed = frac.replace(/0+$/, '');
  return trimmed ? `${grouped},${trimmed}` : grouped;
};
